<!-- CmmTaskListItem.vue -->
<script lang="ts" setup>
import '../../../../../css/choice.css';
import { ref, onBeforeMount, reactive, computed } from 'vue';
import { ButtonDisplaySize, DisplayStyle, ButtonDisplayStyle, TextDisplayStyle, IconTypes } from '../../../../Enums/UiEnums';
import { ActionButtonStyle } from '../../../../lib/Types/Types';
import { setDataQueryComponentId } from '../../../../lib/DataQueryHelper';
import { ChoiceApi, ModalDialog } from '../../../../LibMain';
import { EPATaskStatusType, ITaskItemStatusModel, CoverMyMedsEpaQuestionResponse, CoverMyMedsEpaQuestionAnswerItem } from '../../../../lib/Api/CoverMyMeds';
import { AppScreenNames, DatabaseIdTypes, EnvironmentType, UserGroupType } from '../../../../Enums/ChoiceEnums';
import { areDebugging, serverEnvironment } from '../../../../lib/Environment';
import { ISearchUserGroupItem } from '../../../../lib/Models/SearchModel';
import { confirmDialog, popAlert } from '../../../../lib/Notification';
import { timeLeftString } from '../../../../lib/TimeUtils';
import { addSeconds } from '../../../../lib/DateUtils';
import { isBangUser } from '../../../../lib/ChoiceUtils'
import { IDatabaseIdModel } from '../../../../lib/Models/DatabaseIdModel';
import { showResultError } from '../../../../lib/Models/Result';
import ChoiceButton from '../../../../components/Buttons/ChoiceButton.vue';
import MessageSection from '../../../../components/Messages/MessageSection.vue';
import CmmElectronicPriorAuthorizationQuestionnaire from './CmmElectronicPriorAuthorizationQuestionnaire.vue';
import DataQueryMessage from '../../../../components/Communication/DataQueryMessage.vue';
import FooterBar from '../../../../components/Section/FooterBar.vue'
import MessageBadge from '../../../../components/Messages/MessageBadge.vue';
import Card from '../../../../components/Card.vue';
import CmmQuestionnaireAnswers from './CmmQuestionnaireAnswers.vue';
import ActionButtonV2 from '../../../../components/ActionButtonV2.vue';
import MessageText from '../../../../components/Messages/MessageText.vue';
import FoldingPanel from '../../../../components/FoldingPanel.vue';
import CountDown from '../../../../components/CountDown.vue';
import CmmApproval from './Status/CmmApproval.vue'
import CmmDenial from './Status/CmmDenial.vue'
import CmmClosed from './Status/CmmClosed.vue'
import CmmCancelled from './Status/CmmCancelled.vue'
import CmmPartiallyDenied from './Status/CmmPartiallyDenied.vue'
import PrescriptionDetails from './PrescriptionDetails.vue';
import TitledRow from '../../../../components/TitledRow.vue';
import SearchUserGroup from '../../../../components/Search/SearchUserGroup.vue';
import ModifierKeyMonitor from '../../../../components/ModifierKeyMonitor.vue';
import TextInput from '../../../../components/TextInput.vue';
import Condition from '../../../../components/Condition.vue';
import Row from '../../../../components/Section/Row.vue';
import Icon from '../../../../components/Icon.vue';
import AlertSection from '../../../../components/Messages/AlertSection.vue';



    // DO NOT USE THE PROPS -- we move them into reactive data so we can modify them as needed
    const props = defineProps<{
        taskItemIdType: DatabaseIdTypes.cmm_epa_callback_id|DatabaseIdTypes.cmm_epa_send_id,
        cmmEpaSendId?: number,      
        cmmEpaCallbackId?: number,
        hideFooterBar?: boolean,
        hideQuestionnaire?: boolean
    }>();

    const dataQuerySetup = setDataQueryComponentId();    
    const dataQueryComm = ref<typeof DataQueryMessage>();
    const completedExternallyCommentComp = ref<InstanceType<typeof TextInput>>();

    const compData = reactive<{
        taskItem: {
            taskItemIdType?: DatabaseIdTypes,
            cmmEpaSendId?: number,      
            cmmEpaCallbackId?: number,
        },
        taskStatusInfo?: ITaskItemStatusModel,
        questionSet?: CoverMyMedsEpaQuestionResponse,
        answers?: CoverMyMedsEpaQuestionAnswerItem[],
        bottomVisible?:boolean,
        refreshTaskItemCountdownExpiration?: Date,
        autoCloseWindowCountdownExpiration?: Date,
        showCancellingRequest?: boolean,
        showReAssignModal?: boolean
        showCompletedExternallyModal?: boolean,
        completedExternallyComment?: string,
        newAssigneeId?: IDatabaseIdModel
    }>({
        taskItem: {
            taskItemIdType: DatabaseIdTypes.cmm_epa_callback_id
        }
    });

    onBeforeMount(async ()=> {
        compData.taskItem =
        {
            taskItemIdType: props.taskItemIdType,
            cmmEpaSendId: props.cmmEpaSendId,      
            cmmEpaCallbackId: props.cmmEpaCallbackId
        }
        await loadTaskStatus();
    });

    async function loadTaskStatus()
    {
        if (!compData.taskItem.taskItemIdType)
            return;

        compData.taskStatusInfo = await ChoiceApi.coverMyMeds.getTaskItemStatus(compData.taskItem.taskItemIdType, compData.taskItem.cmmEpaSendId, compData.taskItem.cmmEpaCallbackId);

        if (currentCmmEpaCallbackId.value && 
                (DisplayModeType.ShowQuestionnaire == displayMode.value || DisplayModeType.ShowProcessing == displayMode.value))
        {
            await LoadQuestionsAndAnswers(currentCmmEpaCallbackId.value);
        }

        if (DisplayModeType.ShowProcessing === displayMode.value)
            initializeCountdown();
    }

    // Determine the current 
    const currentCmmEpaCallbackId = computed<number|undefined>(() => {
        return compData.taskStatusInfo?.cmmEpaCallbackId ?? compData.taskItem.cmmEpaCallbackId;
    });

    async function LoadQuestionsAndAnswers(cmmEpaCallbackId: number)
    {
        compData.questionSet = await ChoiceApi.coverMyMeds.getQuestionSet(cmmEpaCallbackId);
        compData.answers = await ChoiceApi.coverMyMeds.getAnswers(cmmEpaCallbackId);
    }

    function closeWindow()
    {
        if (dataQuerySetup.isUsed) {
            dataQueryComm.value?.closeDataQuery({dataQueryComponentId: dataQuerySetup.componentID});
            return;
        }        
        window.close();
    }

    enum DisplayModeType
    {
        ShowQuestionnaire,
        ShowProblem,
        ShowProcessing,
        ShowCompleted,
        ShowPending
    }

    const displayModeMap: Record<EPATaskStatusType, DisplayModeType> =
    {
        [EPATaskStatusType.Open]: DisplayModeType.ShowQuestionnaire,
        [EPATaskStatusType.Followup]: DisplayModeType.ShowQuestionnaire,
        
        [EPATaskStatusType.Problem]: DisplayModeType.ShowProblem,
        [EPATaskStatusType.UnknownState]: DisplayModeType.ShowProblem,
        [EPATaskStatusType.ErrorCallingCMM]: DisplayModeType.ShowProblem,
        [EPATaskStatusType.CMMRejected]: DisplayModeType.ShowProblem,

        [EPATaskStatusType.Initiated]: DisplayModeType.ShowProcessing,
        [EPATaskStatusType.Cancelling]: DisplayModeType.ShowProcessing,
        [EPATaskStatusType.Requested]: DisplayModeType.ShowProcessing,

        [EPATaskStatusType.ApprovedPending]:DisplayModeType.ShowPending,
        [EPATaskStatusType.DeniedPending]:DisplayModeType.ShowPending,
        [EPATaskStatusType.ClosedPending]:DisplayModeType.ShowPending,
        [EPATaskStatusType.PartiallyDeniedPending]:DisplayModeType.ShowPending,

        [EPATaskStatusType.Approved]:DisplayModeType.ShowCompleted,
        [EPATaskStatusType.Denied]:DisplayModeType.ShowCompleted,
        [EPATaskStatusType.Closed]:DisplayModeType.ShowCompleted,
        [EPATaskStatusType.Cancelled]:DisplayModeType.ShowCompleted,
        [EPATaskStatusType.CompletedExternally]:DisplayModeType.ShowCompleted,
        [EPATaskStatusType.PartiallyDenied]:DisplayModeType.ShowCompleted,
    };

    const displayMode = computed<DisplayModeType|undefined>(() => {
        if (undefined === compData.taskStatusInfo?.ePATaskStatusType)
            return;

        return displayModeMap[compData.taskStatusInfo.ePATaskStatusType]
    });

    async function cancelEPA()
    {
        if (!currentCmmEpaCallbackId.value)
        {
            alert("currentCmmEpaCallbackId is invalid");
            return;
        }

        if (false === await confirmDialog("Are you sure you want to cancel the EPA request"))
            return;

        compData.showCancellingRequest = true;
    
        const cancelResp = await ChoiceApi.coverMyMeds.cancelEpaRequestByCallbackId(currentCmmEpaCallbackId.value);
        if (!cancelResp)
        {
            await popAlert("Failure trying to cancel EPA");
            compData.showCancellingRequest = false;
            return;
        }
        if (!cancelResp.passed)
        {
            await popAlert("Failure trying to cancel EPA: " + (cancelResp.message ?? "n/a"));
            compData.showCancellingRequest = false;
            return;            
        }
        compData.autoCloseWindowCountdownExpiration = addSeconds(new Date(), 30);
    }

    function initializeCountdown()
    {
        const countDownInSeconds = areDebugging() ?  10 : 60;
        compData.refreshTaskItemCountdownExpiration = addSeconds(new Date(), countDownInSeconds);
    }

    const showCancelEpaButton = computed<boolean>(()=>{
        if (!currentCmmEpaCallbackId.value)
            return false;

        switch (displayMode.value)
        {
            case DisplayModeType.ShowQuestionnaire:
            case DisplayModeType.ShowProcessing:
            case DisplayModeType.ShowProblem:
            return true;

            default:
                return false;
        }
    });

    const showCompletedExternallyEpaButtonForCallback = computed<boolean>(()=>{
        if (!currentCmmEpaCallbackId.value)
            return false;

        switch (displayMode.value)
        {
            case DisplayModeType.ShowQuestionnaire:
            case DisplayModeType.ShowProcessing:
            case DisplayModeType.ShowProblem:
            return true;

            default:
                return false;
        }
    });

    const showCompletedExternallyEpaButtonForCompleted = computed(()=>{
        return isPendingStatus.value;
    });


    // Resend a message that failed to reach CMM
    async function reSendFailedMessageAttempt()
    {
        if (!compData.taskItem.cmmEpaSendId)
        {
            await popAlert("cmmEpaSendId is invalid");
            return;
        }

        const resp = await ChoiceApi.coverMyMeds.resendMessage(compData.taskItem.cmmEpaSendId);
        if (!resp)
        {
            await popAlert("Unable to resend message");
            return;
        }
        if (!resp.passed)
        {
            await popAlert(`Error trying to resend message: ${resp.message ?? "Unknown error reason"}`);
            return;
        }
        await popAlert("Re-sent to CoverMyMeds.  Reload your task list to see update.");
        closeWindow();
    }

    async function clearProblemMessageAndReDo()
    {
        if (!compData.taskStatusInfo?.taskItemIdType)
        {
            await popAlert("compData.taskStatusInfo?.taskItemIdType is invalid");
            return;
        }
        if (!taskItemId.value)
        {
            await popAlert("problemItemId is invalid");
            return;
        }

        const resp = await ChoiceApi.coverMyMeds.clearProblemMessage(compData.taskStatusInfo.taskItemIdType, taskItemId.value);
        if (!resp)
        {
            await popAlert("Unable to clear problem message");
            return;
        }

        // TODO: Test out
        // Move to the prior item
        compData.taskItem = {
            taskItemIdType: resp.currentIdType,
            cmmEpaCallbackId: resp.currentIdType === DatabaseIdTypes.cmm_epa_callback_id ? resp.currentId : undefined,
            cmmEpaSendId: resp.currentIdType === DatabaseIdTypes.cmm_epa_send_id ? resp.currentId : undefined,
        };

        await popAlert("Cleared problem. Reload your task list to see update.");
        closeWindow();        
    }

    async function answersSent()
    {
        compData.autoCloseWindowCountdownExpiration = addSeconds(new Date(), 30);
    }

    const taskItemId = computed<number | undefined>(() =>{
        switch (compData.taskStatusInfo?.taskItemIdType)
        {
            case DatabaseIdTypes.cmm_epa_callback_id:
            return compData.taskStatusInfo.cmmEpaCallbackId;

            case DatabaseIdTypes.cmm_epa_send_id:
            return compData.taskItem.cmmEpaSendId;

            default:
            return;
        }        
    });

    const statusIndicatesErrorCallingCmm = computed(() =>
    {
        return DatabaseIdTypes.cmm_epa_send_id === compData.taskStatusInfo?.taskItemIdType
            && EPATaskStatusType.ErrorCallingCMM === compData.taskStatusInfo?.ePATaskStatusType;
    });

    const statusIndicatesProblemWeCanClear = computed(()=>{
        if (DatabaseIdTypes.cmm_epa_send_id === compData.taskStatusInfo?.taskItemIdType
                && EPATaskStatusType.CMMRejected === compData.taskStatusInfo?.ePATaskStatusType)
            return true;

        // TODO:  Need to test a callback error state
        // if (DatabaseIdTypes.cmm_epa_callback_id === compData.taskStatusInfo?.taskItemIdType
        //         && EPATaskStatusType.Problem === compData.taskStatusInfo?.ePATaskStatusType)
        //     return true;
        return false;
    });

    const showDiagnosticData = computed(() => {
        if (!isBangUser())
            return false;

        if (areDebugging())
            return true;
        if (EnvironmentType.Dev === serverEnvironment() || EnvironmentType.QA === serverEnvironment())
            return true;
        return false;
    });

    async function newUserOrGroupSelected(newAssignee?: ISearchUserGroupItem)
    {
        compData.newAssigneeId = undefined;
        if (!newAssignee)
            return;

        if (!newAssignee.identifier)
        {
            alert("Invalid newAssignee.identifier");
            return;
        }

        if (newAssignee.optionalIdentifier == UserGroupType.User)
        {
            compData.newAssigneeId = {id: newAssignee.identifier, idType: DatabaseIdTypes.security_users_id }
        }
        if (newAssignee.optionalIdentifier == UserGroupType.Group)
        {
            compData.newAssigneeId = {id: newAssignee.identifier, idType: DatabaseIdTypes.user_group_id }
        }
    }

    async function changeAssignee()
    {
        if (!compData.newAssigneeId)
        {
            alert("Invalid assignee id");
            return;
        }
        if (compData.newAssigneeId?.idType !== DatabaseIdTypes.security_users_id && compData.newAssigneeId?.idType !== DatabaseIdTypes.user_group_id)
        {
            alert("You can only specify a user or group");
            return;
        }

        if (!compData.taskStatusInfo?.message_id)
        {
            alert("Invalid compData.taskStatusInfo.message_id");
            return;
        }

        if (!compData.newAssigneeId.id)
        {
            alert("Invalid assignee ID");
            return;
        }

        const result = await ChoiceApi.coverMyMeds.changeAssignee(compData.taskStatusInfo.message_id, compData.newAssigneeId.idType, +compData.newAssigneeId.id);
        compData.showReAssignModal = false;
        if (!result)
        {
            await popAlert("Issue while trying to change assignee");
            return;
        }
        if (!result.successful)
        {
            await popAlert("Error while trying to change assignee: " + (result.statusDescription ?? "(unknown)"));
            return;
        }

        await popAlert("Update the task queue to show the updated assignee name.");
        closeWindow();
    }

    const showFooterBar = computed(() => !(props.hideFooterBar ?? false));
    const isPendingStatus = computed<boolean>(() =>{
        return DisplayModeType.ShowPending === displayMode.value;
    });

    const allowItemToBeMarkedIncomplete = computed<boolean>(() =>{
        const statusType = compData.taskStatusInfo?.ePATaskStatusType;
        if (statusType === EPATaskStatusType.Closed
            || statusType === EPATaskStatusType.Denied
            || statusType === EPATaskStatusType.Approved
            || statusType === EPATaskStatusType.PartiallyDenied)
            return true;
        return false;
    });

    const wasCompletedExternally = computed<boolean>(()=>{
        return !!compData.taskStatusInfo?.completed_externally_timestamp;
    });

    const completedStatusName = computed<string|undefined>(() => {
        if (DisplayModeType.ShowCompleted === displayMode.value)
        {
            if (wasCompletedExternally.value)
                return "Completed (Externally)";
            return "Completed";
        }
        if (isPendingStatus.value)
            return "Pending";        
    });

    function refreshPage()
    {
        window.location.reload();
    }

    async function onClickCompleteButton()
    {
        const resp = await confirmDialog("Are you sure you want to mark this item 'Complete'");
        if (!resp)
            return;

        if (!currentCmmEpaCallbackId.value)
        {
            alert("currentCmmEpaCallbackId is invalid");
            return;
        }
        
        const result = await ChoiceApi.coverMyMeds.completeItem(currentCmmEpaCallbackId.value);
        if (await showResultError(result, "Unable to mark the task item 'Complete'"))
            return;
        refreshPage();
    }

    async function onClickUndoCompleteButton()
    {
        const resp = await confirmDialog("Are you sure you want to revise this item to 'Pending'");
        if (!resp)
            return;

        if (!currentCmmEpaCallbackId.value)
        {
            alert("currentCmmEpaCallbackId is invalid");
            return;
        }
        
        const result = await ChoiceApi.coverMyMeds.undoCompleteItem(currentCmmEpaCallbackId.value);
        if (await showResultError(result, "Unable to mark the task item 'Pending'"))
            return;
        refreshPage();
    }    

    const showReAssignButton = computed(()=>{
        if (!isPendingStatus.value || displayMode.value === DisplayModeType.ShowPending)
            return true;
        return false;
    });

    function showCompletedExternallyModal()
    {
        compData.completedExternallyComment = undefined;
        compData.showCompletedExternallyModal = true;
    }

    async function updateToCompletedExternally()
    {
        compData.showCompletedExternallyModal = false;

        // Get the ID value
        let id: number|undefined;
        switch (compData.taskItem.taskItemIdType)
        {
            case DatabaseIdTypes.cmm_epa_send_id:
            id = compData.taskItem.cmmEpaSendId;
            break;

            case DatabaseIdTypes.cmm_epa_callback_id:
            id = compData.taskItem.cmmEpaCallbackId;
            break;

            default:
            await popAlert("Invalid taskItemIdType value when marking item 'Completed Externally'");
            return;            
        }
        if (!id)
        {
            await popAlert("Unable to get id to mark item 'Completed Externally'");
            return;
        }

        const resp = await ChoiceApi.coverMyMeds.markCompletedExternally(compData.taskItem.taskItemIdType, id, compData.completedExternallyComment);

        if (!resp?.successful)
        {
            await popAlert(`Error marking the item 'Completed Externally: ${resp?.statusDescription ?? "Not specified"}'`);
        }
     
        await popAlert("Updated. Reload your task list to see update.");
        closeWindow();
    }

</script>

<template>
    <!-- Debugging Info -->
    <ModifierKeyMonitor>
        <template #keydown="{ctrl }">
            <div v-if="showDiagnosticData && (ctrl)">
                <MessageBadge>
                    taskItemIdType: {{ compData.taskItem.taskItemIdType }} 
                    <MessageText :displayStyle="TextDisplayStyle.Muted" :italic="true">
                        {{ compData.taskItem.taskItemIdType == DatabaseIdTypes.cmm_epa_send_id ? "cmm_epa_send_id" : "cmm_epa_callback_id" }}
                    </MessageText>
                    
                </MessageBadge>
                <MessageBadge>
                    cmmEpaSendId: {{ compData.taskItem.cmmEpaSendId }}
                </MessageBadge>
                <MessageBadge>
                    currentCmmEpaCallbackId: {{ currentCmmEpaCallbackId}}
                </MessageBadge>
                <MessageBadge>
                    message_id: {{ compData.taskStatusInfo?.message_id }} => relates_to_message_id: {{ compData.taskStatusInfo?.relates_to_message_id }}
                </MessageBadge>
            </div>
        </template>
    </ModifierKeyMonitor>

    <div v-if="compData.taskStatusInfo">
        <div v-if="DisplayModeType.ShowQuestionnaire === displayMode && currentCmmEpaCallbackId">
            <CmmElectronicPriorAuthorizationQuestionnaire 
                    v-if="!compData.showCancellingRequest"
                    :cmmEpaCallbackId="currentCmmEpaCallbackId"
                    :hideQuestionnaire="props.hideQuestionnaire"
                    @answersSent="answersSent"/>

            <div v-if="compData.showCancellingRequest">
                <MessageText :displayStyle="DisplayStyle.Danger">
                    Cancelling...
                </MessageText>
            </div>

            <CountDown 
                    :expires="compData.autoCloseWindowCountdownExpiration" 
                    v-if="compData.autoCloseWindowCountdownExpiration"
                    @expired="() => { closeWindow(); compData.autoCloseWindowCountdownExpiration = undefined; }">
                <template #format="{remaining}">
                    <MessageText :displayStyle="TextDisplayStyle.Muted">
                        Closing in {{timeLeftString(remaining)}}
                    </MessageText>
                </template>
            </CountDown>
        </div>
        
        <Card v-if="DisplayModeType.ShowProblem === displayMode">
            <template #title>
                Problem
            </template>

            <MessageBadge :displayStyle="DisplayStyle.Danger">
                {{ compData.taskStatusInfo.ePATaskStatus }}
            </MessageBadge>
            <div class="ms-1 me-1">
                {{ compData.taskStatusInfo.problemDescription }}
            </div>

            <hr/>
            <ActionButtonV2 
                    v-if="statusIndicatesErrorCallingCmm" 
                    :action="ActionButtonStyle.Refresh"
                    :displayStyle="DisplayStyle.Secondary"
                    text="Resend"
                    tooltip="Re-send failed message attempt"
                    :performAction="reSendFailedMessageAttempt">
            </ActionButtonV2>

            <ActionButtonV2 
                    v-if="statusIndicatesProblemWeCanClear" 
                    :action="ActionButtonStyle.Refresh"
                    :displayStyle="DisplayStyle.Secondary"
                    text="Redo"
                    tooltip="Re create a message and send to CMM"
                    :performAction="clearProblemMessageAndReDo">
            </ActionButtonV2>            
        </Card>

        <Card v-if="DisplayModeType.ShowProcessing === displayMode">
            <template #title>
                CMM Processing     
            </template>

            <div>
                Message sent to CMM.
                <em class="text-muted">Awaiting a reply...</em>
            </div>
            <MessageBadge>
                Sent on: {{ compData.taskStatusInfo.post_date }}
            </MessageBadge>

            <div class="mb-2 mt-2">
                <CountDown :expires="compData.refreshTaskItemCountdownExpiration" @expired="async () => await loadTaskStatus()">
                    <template #format="{remaining}">
                        <small>
                            <MessageText :displayStyle="TextDisplayStyle.Muted">
                                Will refresh in {{timeLeftString(remaining)}}
                            </MessageText>
                        </small>
                    </template>
                </CountDown>
            </div>

            <!-- Show a link to an attachment if it exists -->
            <AlertSection :displayStyle="DisplayStyle.Warning" v-if="compData.questionSet?.attachmentFileName && props.cmmEpaCallbackId">
                Attachment: 
                <a target="_blank" 
                        :href="ChoiceApi.coverMyMeds.getApprovalAttachmentUrl(props.cmmEpaCallbackId)">
                    <Icon :icon="IconTypes.Attach"/>
                    {{ compData.questionSet?.attachmentFileName }}
                </a>
            </AlertSection>
            
            <!-- Show the answers to questions -->
            <FoldingPanel v-if="compData.questionSet && compData.answers" :panelOpen="false">
                <template #title>
                    Answers to Previous Questions
                </template>
                <template #body>
                    <CmmQuestionnaireAnswers 
                        :answers="compData.answers"
                        :questionSet="compData.questionSet" />                
                </template>
            </FoldingPanel>
        </Card>
        
        <Card v-if="DisplayModeType.ShowCompleted === displayMode || isPendingStatus" 
                :headerDisplayStyle="isPendingStatus ? DisplayStyle.Warning : DisplayStyle.Primary">
            <template #title>
                {{completedStatusName}}
            </template>

            <PrescriptionDetails
                v-if="compData.taskItem.taskItemIdType && taskItemId"
                :taskItemIdType="compData.taskItem.taskItemIdType" 
                :taskItemId="taskItemId" />
            <hr/>

            <div>
                <TitledRow title="Status" >
                    {{ compData.taskStatusInfo.ePATaskStatus }}
                </TitledRow>
            </div>
            <div v-if="wasCompletedExternally">
                <TitledRow title="Completed">
                    {{ compData.taskStatusInfo.completed_externally_timestamp }}
                </TitledRow>
                <TitledRow title="Completed By" >
                    {{ compData.taskStatusInfo.completed_externally_user_id }}
                </TitledRow>                
                <TitledRow title="Comment/Notes">
                    <Condition :condition="(compData.taskStatusInfo.completed_externally_comment?.length ?? 0) > 0">
                        <template #true>
                            {{ compData.taskStatusInfo.completed_externally_comment }}
                        </template>
                        <template #false>
                            <MessageText :displayStyle="TextDisplayStyle.Muted">n/a</MessageText>
                        </template>
                    </Condition>                    
                </TitledRow>                                          
            </div>
            <hr/>

            <template v-if="currentCmmEpaCallbackId">
                <div v-if="EPATaskStatusType.Approved === compData.taskStatusInfo?.ePATaskStatusType || EPATaskStatusType.ApprovedPending === compData.taskStatusInfo?.ePATaskStatusType">
                    <CmmApproval :cmmEpaCallbackId="currentCmmEpaCallbackId" />
                </div>
    
                <div v-if="EPATaskStatusType.Denied === compData.taskStatusInfo?.ePATaskStatusType || EPATaskStatusType.DeniedPending === compData.taskStatusInfo?.ePATaskStatusType">
                    <CmmDenial :cmmEpaCallbackId="currentCmmEpaCallbackId" />
                </div>
    
                <div v-if="EPATaskStatusType.Closed === compData.taskStatusInfo?.ePATaskStatusType || EPATaskStatusType.ClosedPending === compData.taskStatusInfo?.ePATaskStatusType">
                    <CmmClosed :cmmEpaCallbackId="currentCmmEpaCallbackId" />
                </div>
    
                <div v-if="EPATaskStatusType.Cancelled === compData.taskStatusInfo?.ePATaskStatusType">
                    <CmmCancelled :cmmEpaCallbackId="currentCmmEpaCallbackId" />
                </div>

                <div v-if="EPATaskStatusType.PartiallyDenied === compData.taskStatusInfo?.ePATaskStatusType">
                    <CmmPartiallyDenied :cmmEpaCallbackId="currentCmmEpaCallbackId" />
                </div>
            </template>

            <MessageBadge class="mt-2">
                Sent on: {{ compData.taskStatusInfo.post_date }}
            </MessageBadge>
        </Card>

        <div class="mt-1">
            <ActionButtonV2
                    v-if="isPendingStatus" 
                    :action="ActionButtonStyle.Complete"
                    :displayStyle="DisplayStyle.Success"
                    :performAction="onClickCompleteButton" 
                    :endingPadding="1" />

            <ChoiceButton
                v-if="showCompletedExternallyEpaButtonForCompleted" 
                :action="ActionButtonStyle.Complete" 
                :displayStyle="DisplayStyle.Warning"
                text="Completed Externally"
                tooltip="Mark the item as 'Completed Externally'.  This gives you the chance to add a short comment."
                @click="showCompletedExternallyModal"
                :endingPadding="1" />                

            <ActionButtonV2
                    v-if="allowItemToBeMarkedIncomplete" 
                    :action="ActionButtonStyle.Reverse"
                    text="Mark Pending"
                    title="Modify the task item to move from complete back to pending"
                    :displayStyle="DisplayStyle.Success"
                    :performAction="onClickUndoCompleteButton"
                    :endingPadding="1" />
        </div>
    </div>

    <div v-if="!compData.taskStatusInfo">
        <MessageSection :displayStyle="DisplayStyle.Danger">
            There is no status information available
        </MessageSection>
    </div>    


    <!----------------------------------->
    <FooterBar @bottomVisible="visible => compData.bottomVisible = visible"
            :backgroundColor="false === compData.bottomVisible ? 'lightgoldenrodyellow' : undefined"
            v-if="showFooterBar">
        <template #left>
            <ActionButtonV2 
                v-if="showCancelEpaButton" 
                :action="ActionButtonStyle.Delete" 
                :displayStyle="ButtonDisplayStyle.OutlineDanger"
                :size="ButtonDisplaySize.Small"
                text="Cancel ePA"
                :performAction="cancelEPA"
                :endingPadding="1" />

            <ChoiceButton
                v-if="showCompletedExternallyEpaButtonForCallback" 
                :action="ActionButtonStyle.Complete" 
                :displayStyle="DisplayStyle.Warning"
                :size="ButtonDisplaySize.Small"
                text="Completed Externally"
                tooltip="Mark the item as 'Completed Externally'.  This gives you the chance to add a short comment."
                @click="showCompletedExternallyModal"
                :endingPadding="1" />

            <MessageText v-if="false === compData.bottomVisible" :displayStyle="TextDisplayStyle.Muted">
                <em>Scroll down to see more...</em>
            </MessageText>
        </template>
        <template #right>
            <ChoiceButton 
                v-if="showReAssignButton"
                :action="ActionButtonStyle.Edit"
                text="Re-Assign"
                tooltip="Change Assignee"
                :size="ButtonDisplaySize.Small"
                :displayStyle="ButtonDisplayStyle.OutlinePrimary"
                @click="() => { compData.showReAssignModal = true; compData.newAssigneeId = undefined; }" />

            <ChoiceButton
            :action="ActionButtonStyle.Close"
            tooltip="Close CMM Task Item"
            :size="ButtonDisplaySize.Small"
            :displayStyle="DisplayStyle.Primary" 
            @click="closeWindow()"/>
        </template>
    </FooterBar>

    <div v-if="dataQuerySetup.isUsed">
        <DataQueryMessage ref="dataQueryComm" :dataQueryParent="false" :componentID="dataQuerySetup.componentID" /> 
    </div> 

    <ModalDialog v-model:show="compData.showReAssignModal">
        <template #title>
            Change Assignee
        </template>
        <SearchUserGroup 
            :screenName="AppScreenNames.CoverMyMedsEpaQueue"
            :enableAutoSearch="true"
            @onChange="newUserOrGroupSelected"/>
        <template #footer>
            <ActionButtonV2 
                    :action="ActionButtonStyle.Save" 
                    :performAction="changeAssignee" 
                    :disabled="!compData.newAssigneeId"
                    :displayStyle="DisplayStyle.Success"/>
        </template>
    </ModalDialog>

    <ModalDialog v-model:show="compData.showCompletedExternallyModal" @shown="() => completedExternallyCommentComp?.focus()">
        <template #title>
            Add Completed Externally Comment
        </template>

        Notes/Comments
        <TextInput ref="completedExternallyCommentComp" v-model:textValue="compData.completedExternallyComment" :maxLength="100"/>

        <template #footer>
            <ActionButtonV2 
                    :action="ActionButtonStyle.Save" 
                    title="Mark the item 'Completed Externally'"
                    :performAction="updateToCompletedExternally" 
                    :displayStyle="DisplayStyle.Success"/>
        </template>

    </ModalDialog>
</template>
